import React, { useState, useEffect, useCallback } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./tokenlyglobal.css";

export default function Reports() {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [doctorsList, setDoctorsList] = useState([]);
  const [dataTable, setDataTable] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState("");

  const tableHeaders = ['Date', 'Clinic', 'Free', 'Paid', 'Total'];

  const getData = useCallback(async () => {
    setIsLoading(true);
    setError("");
    
    try {
      const token = localStorage.getItem("tokenly_token");
      const response = await fetch("https://tokenly.biswaas.com/api/doctor/report", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          dateMonth: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
          selectedDoctors: selectedDoctors
        })
      });

      const json = await response.json();
      
      if (json.status) {
        handleResponse(json.data, false, json.message, 'getData');
      } else {
        handleResponse(null, true, json.message || "Failed to fetch report data", 'getData');
      }
    } catch (e) {
      handleResponse(null, true, e.message || "Error fetching report data", 'getData');
    }
  }, [selectedDate, selectedDoctors]);

  const getStaffDoctorsList = useCallback(async () => {
    try {
      const token = localStorage.getItem("tokenly_token");
      const response = await fetch("https://tokenly.biswaas.com/api/doctor/doctorDashboardDataV15", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({})
      });

      const json = await response.json();
      
      if (json.status) {
        handleResponse(json.data, false, json.message, 'staffDoctorsList');
      } else {
        handleResponse(null, true, json.message || "Failed to fetch doctors list", 'staffDoctorsList');
      }
    } catch (e) {
      handleResponse(null, true, e.message || "Error fetching doctors list", 'staffDoctorsList');
    }
  }, []);

  const handleResponse = (data, error, msg, flag) => {
    if (error) {
      setError(msg || "An error occurred");
    } else {
      switch (flag) {
        case 'getData':
          setIsRefreshing(false);
          let aa = data.rows.map(function (item, i) {
            return [
              item.cdate,
              item.totalToken - (item.onlineToken + item.onlineFreeToken),
              item.onlineFreeToken !== null ? item.onlineFreeToken : 0,
              item.onlineToken !== null ? item.onlineToken : 0,
              item.totalToken,
            ];
          });

          let onlineFreeTokenTotal = 0;
          let onlineTokenTotal = 0;
          let totalTokenTotal = 0;

          data.rows.forEach(funds => {
            onlineFreeTokenTotal += parseFloat(
              funds.onlineFreeToken !== null ? funds.onlineFreeToken : 0,
            );
            onlineTokenTotal += parseFloat(
              funds.onlineToken !== null ? funds.onlineToken : 0,
            );
            totalTokenTotal += parseFloat(
              funds.totalToken !== null ? funds.totalToken : 0,
            );
          });

          let clinicTokenTotal =
            totalTokenTotal - (onlineFreeTokenTotal + onlineTokenTotal);

          aa.push([
            'Sum',
            clinicTokenTotal,
            onlineFreeTokenTotal,
            onlineTokenTotal,
            totalTokenTotal,
          ]);

          setDataTable(aa);
          setIsLoading(false);
          break;
        case 'staffDoctorsList':
          setDoctorsList(data);
          break;
        default:
          console.warn('Unknown response flag:', flag);
          break;
      }
    }
    setIsLoading(false);
  };

  const handleDoctorSelection = (doctorId) => {
    setSelectedDoctors(prev => {
      if (prev.includes(doctorId)) {
        return prev.filter(id => id !== doctorId);
      } else {
        return [...prev, doctorId];
      }
    });
  };

  // Get user from localStorage
  useEffect(() => {
    const stored = localStorage.getItem("tokenly_user");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setCurrentUser(parsed);
      } catch (e) {
        console.error("Error parsing user data:", e);
      }
    }
  }, []);

  // Check if user is clinic and get doctors list
  useEffect(() => {
    if (currentUser && currentUser.is_clinic === 1) {
      getStaffDoctorsList();
    }
  }, [currentUser, getStaffDoctorsList]);

  // Fetch data when date or selected doctors change
  useEffect(() => {
    if (currentUser) {
      getData();
    }
  }, [selectedDate, selectedDoctors, currentUser, getData]);

  const onRefresh = useCallback(() => {
    setIsRefreshing(true);
    setSelectedDoctors([]);
    setSelectedDate(new Date());
    getData();
  }, [getData]);

  if (!currentUser) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading user information...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container-fluid container-xl px-4 px-md-3">
          <h1 className="display-5 fw-bold text-white mb-1">Reports</h1>
          <p className="text-light mb-0">Monthly token and clinic reports — filter by doctor and month.</p>
        </div>
      </section>

      <div className="container pt-3 pt-md-4" style={{ minHeight: 'calc(100vh - 260px)' }}>
        <div className="row">
          <div className="col-12">
          
          {/* Doctor Selection (Clinic Only) */}
          {currentUser.is_clinic === 1 && (
            <div className="mb-3">
              <label className="form-label">Select Doctors:</label>
              <div className="border rounded p-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                {doctorsList.length > 0 ? (
                  doctorsList.map(doctor => (
                    <div key={doctor.id} className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`doctor-${doctor.id}`}
                        checked={selectedDoctors.includes(doctor.id)}
                        onChange={() => handleDoctorSelection(doctor.id)}
                      />
                      <label className="form-check-label" htmlFor={`doctor-${doctor.id}`}>
                        {doctor.fullname}
                      </label>
                    </div>
                  ))
                ) : (
                  <p className="text-muted">No doctors available</p>
                )}
              </div>
            </div>
          )}

          {/* Month Year Picker */}
          <div className="mb-3">
            <label className="form-label">Select Month & Year:</label>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              dateFormat="MMMM yyyy"
              showMonthYearPicker
              showFullMonthYearPicker
              className="form-control"
              maxDate={new Date()}
              minDate={new Date(2023, 0, 1)}
              placeholderText="Select month and year"
            />
          </div>

          {/* Refresh Button */}
          <button 
            className="btn btn-outline-primary mb-3"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                Refreshing...
              </>
            ) : (
              'Refresh'
            )}
          </button>

          {/* Error Message */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Loading Spinner */}
          {isLoading && (
            <div className="text-center my-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading report data...</p>
            </div>
          )}

          {/* Report Table */}
          {!isLoading && dataTable.length > 0 && (
            <div className="table-responsive">
              <table className="table table-bordered table-striped">
                <thead className="table-dark">
                  <tr>
                    {tableHeaders.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataTable.map((row, rowIndex) => (
                    <tr key={rowIndex} className={row[0] === 'Sum' ? 'table-info fw-bold' : ''}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* No Data Message */}
          {!isLoading && dataTable.length === 0 && !error && (
            <div className="text-center my-5">
              <p className="text-muted">No report data available for selected period.</p>
            </div>
          )}
          </div>
        </div>
      </div>
    </>
  );
}